/**
 * localStorage persistence for the active gameplay session — backs
 * the spec §5.3 mid-session resume rule.
 *
 * Snapshot semantics:
 *   - Saved on every meaningful change to (ActiveSession, current phase)
 *     during gameplay
 *   - Loaded on app boot; if `savedAt` is more than 10 minutes ago the
 *     snapshot is silently dropped and load() returns null (Eva's
 *     "session" effectively starts fresh)
 *   - Cleared explicitly on celebration sign-off and on "Start over"
 *     from the resume modal
 *
 * Storage shape is opaque to callers — we hide it behind save/load/clear
 * so we can rev the key (the v1 suffix below) without rippling callers.
 *
 * Failures (SSR, private-mode quotas, JSON parse errors) all fall
 * through to "no snapshot" rather than throwing — Eva should never see
 * the app blow up because storage was unhappy.
 */
import type { AppScreen } from "@/context/NavigationContext";
import type { ActiveSession } from "@/context/SessionContext";

const STORAGE_KEY = "eva.session.v1";
const TTL_MS = 10 * 60 * 1000; // spec §5.3 — 10 minutes since last interaction

export interface SessionSnapshot {
  session: ActiveSession;
  phase: AppScreen;
  savedAt: number;
}

/** Resumable phases — every gameplay screen between flash card and the
 *  3x-write completion. Celebration is intentionally excluded: by the
 *  time she's there the session has been POSTed, so "resume" would
 *  re-celebrate or worse re-POST. Phase 6's sign-off clears the
 *  snapshot. */
export const RESUMABLE_PHASES: ReadonlySet<AppScreen> = new Set<AppScreen>([
  "flashCard",
  "phaseIntro:firstPass",
  "firstPass",
  "results",
  "phaseIntro:secondChance",
  "secondChance",
  "phaseIntro:threeXWrite",
  "threeXWrite",
]);

export function saveSnapshot(session: ActiveSession, phase: AppScreen): void {
  if (typeof window === "undefined") return;
  try {
    const payload: SessionSnapshot = {
      session,
      phase,
      savedAt: Date.now(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / SSR / disabled storage — ignore silently. Eva isn't
    // promised resume; she just won't get it this time.
  }
}

export function loadSnapshot(): SessionSnapshot | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed: SessionSnapshot;
  try {
    parsed = JSON.parse(raw) as SessionSnapshot;
  } catch {
    clearSnapshot();
    return null;
  }
  // Sanity-check the shape — anything else is treated as corrupt
  // (likely an older schema we shouldn't try to revive).
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    typeof parsed.savedAt !== "number" ||
    typeof parsed.phase !== "string" ||
    typeof parsed.session !== "object" ||
    parsed.session === null
  ) {
    clearSnapshot();
    return null;
  }
  if (Date.now() - parsed.savedAt > TTL_MS) {
    clearSnapshot();
    return null;
  }
  return parsed;
}

export function clearSnapshot(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
